import { CloudPolicy, CloudPolicyType, CloudType, PolicyStatus } from '../types/policies';

const severityLabels: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map(item => (item === null || item === undefined ? '' : String(item).trim()))
    .filter(Boolean);
};

const toTargetArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const raw = value.map(item => (item === null || item === undefined ? '' : String(item).trim()));
  if (raw.some(item => item === '' || item === '*')) return ['*'];
  return raw.filter(Boolean);
};

export const normalizePolicySeverity = (severity: unknown): string => {
  const raw = String(severity || 'Medium').trim();
  return severityLabels[raw.toLowerCase()] || raw || 'Medium';
};

export const normalizeExecutionHistory = (history: unknown): any[] => {
  if (Array.isArray(history)) return history;
  if (!history || typeof history !== 'object') return [];

  return Object.entries(history as Record<string, any>)
    .map(([key, value], index) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Handle new structured format with mode, status, and steps
        if ('mode' in value || 'steps' in value) {
          return {
            id: key,
            mode: value.mode || 'UNKNOWN',
            result: (value.status || 'success').toLowerCase(),
            steps: Array.isArray(value.steps) ? value.steps : [],
            // Keep timestamp if it exists, otherwise it will just be omitted
            timestamp: value.timestamp || value.executedAt || value.createdAt,
          };
        }

        // Handle legacy format
        return {
          id: value.id || key || `execution-${index}`,
          timestamp: value.timestamp || value.executedAt || value.createdAt || new Date().toISOString(),
          actionTaken: value.actionTaken || value.action || value.operation || 'Policy execution',
          targetEntity: value.targetEntity || value.target || value.resourceId || 'Unknown target',
          entityType: value.entityType || value.resourceType || 'resource',
          result: (value.result || value.status || 'success').toLowerCase(),
          details: value.details || value.message || value.description || '',
          triggeredBy: value.triggeredBy,
          ...value,
        };
      }

      return {
        id: key || `execution-${index}`,
        timestamp: new Date().toISOString(),
        actionTaken: 'Policy execution',
        targetEntity: String(value || 'Unknown target'),
        entityType: 'resource',
        result: 'success',
        details: '',
      };
    })
    .filter(Boolean);
};

export const getPolicyCommands = (policyData: any): any[] => {
  if (!policyData || typeof policyData !== 'object') return [];
  if (Array.isArray(policyData.commands)) return policyData.commands;
  if (Array.isArray(policyData.actions)) return policyData.actions;
  return [];
};

export const normalizeCloudPolicy = (
  policy: any,
  fallbackIndex = 0,
  fallbackCloudType?: CloudType,
): CloudPolicy => {
  const policyId = policy?.policyId ? String(policy.policyId) : undefined;
  const id = policy?.id ?? policyId ?? `policy-${Date.now()}-${fallbackIndex}`;
  const cloudPolicyType = (policy?.cloudPolicyType || 'GUARDRAIL') as CloudPolicyType;
  const policyStatus = (policy?.policyStatus || 'INACTIVE') as PolicyStatus;

  return {
    ...policy,
    id,
    policyId,
    title: policy?.title || 'Untitled policy',
    description: policy?.description || '',
    severity: normalizePolicySeverity(policy?.severity),
    cloudPolicyType,
    policyStatus,
    includedUsers: toTargetArray(policy?.includedUsers),
    includedNHIs: toTargetArray(policy?.includedNHIs),
    includedResources: toTargetArray(policy?.includedResources),
    excludedUsers: toTargetArray(policy?.excludedUsers),
    excludedNHIs: toTargetArray(policy?.excludedNHIs),
    excludedResources: toTargetArray(policy?.excludedResources),
    policyTargetTypes: Array.isArray(policy?.policyTargetTypes) ? policy.policyTargetTypes : (policy?.policyTargetType ? [policy.policyTargetType] : []),
    cloudType: (policy?.cloudType || fallbackCloudType || 'AWS') as CloudType,
    policyEnableData: policy?.policyEnableData || {},
    policyDisableData: policy?.policyDisableData || {},
    executionHistory: normalizeExecutionHistory(policy?.executionHistory),
    policyMetadata: policy?.policyMetadata || {},
    linkedAlerts: toStringArray(policy?.linkedAlerts),
    isDefault: Boolean(policy?.isDefault),
    tenantName: policy?.tenantName,
    cloudAccountId: policy?.cloudAccountId,
  };
};

export const normalizeCloudPolicies = (policies: any, fallbackCloudType?: CloudType): CloudPolicy[] => {
  if (!Array.isArray(policies)) return [];
  return policies.map((policy, index) => normalizeCloudPolicy(policy, index, fallbackCloudType));
};
